import React from 'react';

import clsx from 'classnames';
import PropTypes from 'prop-types';

import {
    capitalize,
    combineClasses,
} from 'wanderu-css';

import styles from './Typography.scss';

const defaultVariantMapping = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    subtitle: 'h6',
    body1: 'p',
    body2: 'p',
};

function Typography(props) {
    const {
        align = 'inherit',
        classes: classesProp = {},
        className,
        color = 'initial',
        component: ComponentProp,
        componentProps,
        display = 'initial',
        noWrap = false,
        paragraph = false,
        variant = 'body1',
        ...otherProps
    } = props;

    const Component = ComponentProp || defaultVariantMapping[variant] || 'span';

    const classes = combineClasses(styles, classesProp);

    return (
        <Component
            className={clsx(
                classes.root,
                {
                    [classes[`color${capitalize(color)}`]]: color !== 'initial',
                    [classes[`align${capitalize(align)}`]]: align !== 'inherit',
                    [classes[`display${capitalize(display)}`]]: display !== 'initial',
                    [classes[variant]]: variant !== 'inherit',
                    [classes.noWrap]: noWrap,
                    [classes.paragraph]: paragraph,
                },
                className,
            )}
            {...otherProps}
            {...componentProps}
        />
    );
}

Typography.propTypes = {
    /**
     * Set the text-align on the component.
     */
    align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
    /**
     * The content of the component.
     */
    children: PropTypes.node.isRequired,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The color of the component. It supports those theme colors that make sense for this component.
     */
    color: PropTypes.oneOf([
        'initial',
        'inherit',
        'primary',
        'secondary',
        'error',
    ]),
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * The set of components passed down to the root component
     */
    componentProps: PropTypes.object,
    /**
     * The display property of the component.
     */
    display: PropTypes.oneOf(['initial', 'block', 'inline']),
    /**
     * If `true`, the text will not wrap, but instead will truncate with a text overflow ellipsis.
     */
    noWrap: PropTypes.bool,
    /**
     * If `true`, the text will have a bottom margin.
     */
    paragraph: PropTypes.bool,
    /**
     * Used to control the text styling.
     */
    variant: PropTypes.oneOf(['inherit', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body1', 'body2', 'subtitle', 'button', 'caption', 'overline']),
};

export default Typography;
